// lib/takeoff/rfiReport.js — an RFI as a clean printable page (Print / Save as PDF) and as e-mail text.
import { openPrintWindow } from "./estimateReport";

const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
const day = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "—";
export const rfiNo = (r) => `RFI-${String(r?.number || 0).padStart(3, "0")}`;

const asked = (rfi) =>
  (rfi.items || []).filter((i) => String(i.question || "").trim());
export function rfiEmail(rfi, meta = {}) {
  const subject = `${rfiNo(rfi)} — ${rfi.title || "Request for information"}${meta.project ? ` — ${meta.project}` : ""}`;
  const lines = [
    `${rfi.to?.name ? `Hi ${rfi.to.name.split(" ")[0]},` : "Hello,"}`,
    "",
    rfi.message ||
      `Please see our request for information below${rfi.dueDate ? ` and reply by ${day(rfi.dueDate)}` : ""}.`,
    "",
    ...asked(rfi).flatMap((it, i) => [
      `${i + 1}. ${it.question}`,
      ...(it.source?.label ? [`   Reference: ${it.source.label}`] : []),
      ...(it.assumption
        ? [`   Unless advised otherwise we will assume: ${it.assumption}`]
        : []),
      ...(it.images?.length
        ? [
            `   (${it.images.length} picture${it.images.length === 1 ? "" : "s"} in the attached PDF)`,
          ]
        : []),
      "",
    ]),
    `Thank you,`,
    meta.preparedBy || "",
    meta.company || "",
  ];
  return { subject, body: lines.join("\n") };
}

export function mailtoUrl(rfi, meta) {
  const { subject, body } = rfiEmail(rfi, meta);
  const to = rfi.to?.email || "";
  const cc = (rfi.cc || []).join(",");
  // (mail programs cut very long links — the questions are short enough, the drawing pins stay in the PDF)
  return `mailto:${encodeURIComponent(to)}?${cc ? `cc=${encodeURIComponent(cc)}&` : ""}subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body).slice(0, 1800 * 3)}`;
}

/** rfi → printable HTML. images: { [file]: dataURL } — the questions' pictures (see imagesForPrint) */
export function buildRfiHtml(rfi, meta = {}, images = {}) {
  const pinsFor = (it) => (rfi.pins || []).filter((p) => p.itemId === it.id);
  const items = asked(rfi)
    .map((it, i) => {
      const pics = (it.images || []).filter((im) => images[im.file]);
      const shots = pics
        .map(
          (im, k) =>
            `<figure class="${pics.length === 1 ? "one" : ""}"><img src="${images[im.file]}" class="shot"><figcaption>Fig. ${i + 1}.${k + 1}${im.caption ? ` — ${esc(im.caption)}` : ""}${im.source === "drawing" && im.page ? ` <span class="muted">(drawing page ${im.page})</span>` : ""}</figcaption></figure>`,
        )
        .join("");
      return `<div class="q">
        <div class="qh"><span class="qn">${i + 1}</span><div class="qt">${esc(it.question).replace(/\n/g, "<br>")}</div></div>
        ${it.source?.label ? `<div class="ref">Reference: ${esc(it.source.label)}${pinsFor(it).length ? ` · marked on the drawing` : ""}</div>` : ""}
        ${it.assumption ? `<div class="as"><b>Our assumption if not advised:</b> ${esc(it.assumption)}</div>` : ""}
        ${shots ? `<div class="shots">${shots}</div>` : ""}
        <div class="resp"><div class="rl">Response</div>${it.response ? `<div class="rt">${esc(it.response).replace(/\n/g, "<br>")}</div>` : '<div class="blank"></div><div class="blank"></div>'}</div>
      </div>`;
    })
    .join("");
  const row = (l, v) => `<tr><td>${esc(l)}</td><td>${v}</td></tr>`;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(rfiNo(rfi))} — ${esc(rfi.title)}</title>
<style>
  @page { size: letter; margin: 14mm 12mm 16mm;
    @bottom-right { content: "${esc(rfiNo(rfi))} · Page " counter(page) " of " counter(pages); font: 8pt Arial, sans-serif; color: #888; } }
  * { box-sizing: border-box; }
  body { font-family: Inter, "Segoe UI", Arial, sans-serif; color: #1f2328; font-size: 10pt; margin: 0; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page { max-width: 8.1in; margin: 0 auto; padding: .3in .1in; }
  .top { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #f97316; padding-bottom: 10px; }
  .co { font-size: 15pt; font-weight: 700; } .muted { color: #888; }
  .doct { font-size: 18pt; font-weight: 800; letter-spacing: .04em; color: #f97316; text-align: right; }
  .no { text-align: right; font-size: 12pt; font-weight: 700; }
  table.meta { width: 100%; border-collapse: collapse; margin: 12px 0; }
  table.meta td { padding: 4px 8px; border: 1px solid #e3e5e8; vertical-align: top; }
  table.meta td:first-child { width: 22%; background: #f6f7f9; font-size: 8pt; text-transform: uppercase; letter-spacing: .06em; color: #666; }
  .subj { font-size: 12pt; font-weight: 700; margin: 14px 0 6px; }
  .msg { margin: 0 0 10px; white-space: pre-line; }
  .q { border: 1px solid #e3e5e8; border-radius: 8px; margin: 10px 0; padding: 10px 12px; break-inside: avoid; }
  .qh { display: flex; gap: 10px; } .qn { flex: none; width: 22px; height: 22px; border-radius: 50%; background: #f97316; color: #fff; font-weight: 700; display: flex; align-items: center; justify-content: center; font-size: 9pt; }
  .qt { font-weight: 600; }
  .ref { margin: 6px 0 0 32px; font-size: 8.5pt; color: #666; }
  .as { margin: 6px 0 0 32px; font-size: 9pt; background: #fffbeb; border-left: 3px solid #f59e0b; padding: 4px 8px; }
  .shots { margin: 8px 0 0 32px; display: flex; gap: 10px; flex-wrap: wrap; }
  .shots figure { margin: 0; width: calc(50% - 5px); break-inside: avoid; } .shots figure.one { width: 100%; }
  .shot { width: 100%; max-height: 5.2in; object-fit: contain; border: 1px solid #ddd; border-radius: 4px; background: #fff; }
  figcaption { font-size: 8.5pt; color: #555; margin-top: 3px; }
  .resp { margin: 8px 0 0 32px; } .rl { font-size: 7.5pt; text-transform: uppercase; letter-spacing: .08em; color: #888; }
  .rt { background: #f0fdf4; border-left: 3px solid #22c55e; padding: 4px 8px; }
  .blank { border-bottom: 1px solid #bbb; height: 22px; }
  .sig { display: flex; gap: 40px; margin-top: 28px; break-inside: avoid; } .sig > div { flex: 1; } .line { border-bottom: 1px solid #333; height: 30px; }
  .toolbar { position: sticky; top: 0; background: #111827; color: #fff; padding: 8px 14px; display: flex; align-items: center; font-size: 10pt; }
  .toolbar button { margin-left: auto; background: #f97316; color: #fff; border: 0; border-radius: 6px; padding: 6px 14px; font-weight: 600; cursor: pointer; }
  @media print { .toolbar { display: none; } .page { padding: 0; } }
</style></head><body>
<div class="toolbar">${esc(rfiNo(rfi))} preview — use “Save as PDF” in the print dialog to attach it to an e-mail.<button onclick="window.print()">Print / Save PDF</button></div>
<div class="page">
  <div class="top"><div><div class="co">${esc(meta.company || "")}</div><div class="muted">${esc(meta.companyLine || "")}</div></div>
  <div><div class="doct">REQUEST FOR INFORMATION</div><div class="no">${esc(rfiNo(rfi))}</div></div></div>
  <table class="meta">
    ${row("Project", esc(meta.project || "—"))}
    ${row("To", esc([rfi.to?.name, rfi.to?.company].filter(Boolean).join(", ") || "—") + (rfi.to?.email ? ` <span class="muted">· ${esc(rfi.to.email)}</span>` : ""))}
    ${rfi.cc?.length ? row("CC", esc(rfi.cc.join(", "))) : ""}
    ${row("From", esc([meta.preparedBy, meta.company].filter(Boolean).join(", ") || "—"))}
    ${row("Date issued", esc(day(rfi.sentAt || new Date())))}
    ${row("Response required by", `<b>${esc(day(rfi.dueDate))}</b>`)}
    ${row("Trade", "Drywall / steel framing / ceilings")}
    ${row("Impact", `Cost: ${esc(rfi.costImpact || "unknown")} · Schedule: ${esc(rfi.scheduleImpact || "unknown")}`)}
  </table>
  <div class="subj">${esc(rfi.title || "Request for information")}</div>
  ${rfi.message ? `<p class="msg">${esc(rfi.message)}</p>` : ""}
  ${items}
  <div class="sig"><div><div class="line"></div><div class="muted">Response by (name, company)</div></div><div><div class="line"></div><div class="muted">Date</div></div></div>
</div></body></html>`;
}

// win: a window opened by the click itself (pictures load first — a window opened after waiting gets blocked)
export function printRfi(rfi, meta, images, win = null) {
  if (!win) return openPrintWindow(buildRfiHtml(rfi, meta, images));
  win.document.open();
  win.document.write(buildRfiHtml(rfi, meta, images));
  win.document.close();
  win.focus();
  setTimeout(() => {
    try {
      win.print();
    } catch {
      /* the preview has its own Print button */
    }
  }, 600);
  return true;
}
